import React, { useContext, useEffect, useState } from "react";
import Logo from "../../assets/img/skorLogo.svg";
import Flag from "../../assets/img/flag.svg";
import useTranslator from "../../hooks/useTranslator";
import jwtDecode from "jwt-decode";
import { Link, useNavigate } from "react-router-dom";
import EmailForm from "../../components/Auth/EmailForm";
import EnterOtpModal from "../onboarding/enterOtpModal";
import BasicInfoModal from "../onboarding/BasicInfoModal";
import constants from "../../Utils/constants";
import axios from "axios";
import { detectIdentityType, haveValue, toast } from "../../helper/common";
import TenantContext from "../../context/tenant/TenantContext";
import AuthContext from "../../context/auth/AuthContext";
import { useCookies } from "react-cookie";
import { trackClarityEvent } from "../../Utils/clarityReady";

const TenantAuth = ({
  onChangeHandler,
  disabled,
  emailSubmitHandler,
  currentScreen,
  setCurrentScreen,
  sendOtpFun,
  otp,
  setOtp,
  showResend,
  showTimer,
  currentLang,
  handleLanguageChange,
  verifyOtpHandler,
  userLogin,
  setUserLogin,
  runTimer,
  companyLogo,
  redirectFun,
  verifyOtpLoading,
  from,
  landlordsData,
}) => {
  const { _t } = useTranslator();
  const { getUserByEmail } = useContext(AuthContext);
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const { submitProtectedHousingDocuments } = useContext(TenantContext);
  const [cookies, setCookie, removeCookie] = useCookies([
    "sharedProfileLink",
    "libeenWorkspaceTenant",
    "catellaWorkspaceTenant",
    "invitationToken",
    "invitedEmail",
  ]);
  const [invitationToken, setInvitationToken] = useState(null);
  const [isInvitedEmail, setIsInvitedEmail] = useState(false);

  useEffect(() => {
    const checkInvitation = async () => {
      if (haveValue(cookies?.invitationToken)) {
        try {
          const decoded = jwtDecode(cookies.invitationToken);

          if (decoded.exp && decoded.exp * 1000 < Date.now()) {
            removeCookie("invitationToken", { path: "/", maxAge: 1800 });
            removeCookie("requestProfileLink", { path: "/", maxAge: 1800 });
            navigate("/tenant/auth");
            return;
          }

          if (haveValue(decoded?.email)) {
            setUserLogin((prevData) => ({
              ...prevData,
              email: decoded.email,
            }));

            const ifUserAlreadyExists = await getUserByEmail({
              email: decoded.email,
              type: "tenant",
            });

            if (ifUserAlreadyExists?.user) {
              return;
            }

            setCurrentScreen("registerUser");
            trackClarityEvent("tenant_onboarding_started");

            setTimeout(() => {
              trackClarityEvent("tenant_basic_info_step_started");
            }, constants.CLARITY_DELAY);

            setInvitationToken(cookies?.invitationToken);
          }
        } catch (err) {
          removeCookie("invitationToken", { path: "/", maxAge: 1800 });
          removeCookie("requestProfileLink", { path: "/", maxAge: 1800 });
          navigate("/tenant/auth");
        }
      }
    };

    checkInvitation();
  }, [cookies?.invitationToken]);

  useEffect(() => {
    const checkInvitedEmail = async () => {
      if (haveValue(cookies?.invitedEmail)) {
        const email = cookies.invitedEmail;
        setUserLogin((prevData) => ({
          ...prevData,
          email,
        }));

        const ifUserAlreadyExists = await getUserByEmail({
          email,
          type: "tenant",
        });

        if (ifUserAlreadyExists?.user) {
          return;
        }

        setIsInvitedEmail(true);
        setCurrentScreen("registerUser");
        trackClarityEvent("tenant_onboarding_started");
        setTimeout(() => {
          trackClarityEvent("tenant_basic_info_step_started");
        }, constants.CLARITY_DELAY);
      }
    };

    checkInvitedEmail();
  }, [cookies?.invitedEmail]);

  useEffect(() => {
    if (localStorage.getItem("usertype") == "tenant") {
      setUserLogin((prevData) => ({
        ...prevData,
        phone_number: phoneNumber?.replace("+", ""),
      }));
    } else {
      setUserLogin((prevData) => ({ ...prevData, phone: phoneNumber }));
    }
  }, [phoneNumber]);

  const [selectedProtectedHousingDocs, setSelectedProtectedHousingDocs] =
    useState([]);

  const requiresSubcategory = (status) => {
    return [
      constants.public_servant,
      constants.employed,
      constants.self_employed,
    ].includes(status);
  };

  const submitHandler = async (e, verifyOtp) => {
    try {
      if (haveValue(invitationToken) || isInvitedEmail) {
        await registerUser();
      } else if (!verifyOtp) {
        setCurrentScreen("verifyOTP");
        sendOtpFun();
        runTimer();
      } else {
        await verifyOtpHandler(e, registerUser);
      }
    } catch (error) {}
  };

  const registerUser = async () => {
    try {
      if (isInvitedEmail) {
        removeCookie("invitedEmail", { path: "/" });
      }
      const identity_type = detectIdentityType(userLogin?.identity_number);

      let protected_housing = false;
      if (!selectedProtectedHousingDocs.includes("no")) {
        protected_housing = true;
      }

      const registerData = {
        email: userLogin.email,
        password: userLogin.password,
        confirmPassword: userLogin.confirmPassword,
        type: userLogin.type,
        checked: true,
        qrCode_signup: false,
        analysisCompleted: true,
        user_type: userLogin.user_type,
        qrCode_signup: userLogin?.qrCode_signup,
        requestProfileLink: userLogin?.requestProfileLink,
        fname: userLogin?.fname,
        lname: userLogin?.lname,
        dni_nie:
          identity_type === "dni" || identity_type === "nie"
            ? userLogin?.identity_number
            : null,
        passport_id:
          identity_type === "passport" ? userLogin?.identity_number : null,
        post_code: userLogin?.post_code,
        dob: userLogin?.dob,
        employment_status: userLogin?.employment_status,
        employment_category: userLogin?.employment_category,
        current_annual_income: userLogin?.current_annual_income,
        currency: userLogin?.currency,
        protected_housing: userLogin?.protected_housing,
        phone_number: userLogin?.phone_number,
        lang: currentLang,
        need_password: false,
        skip_steps: userLogin?.skip_steps,
      };
      if (haveValue(invitationToken)) {
        registerData.invitationToken = invitationToken;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/signup`,
        registerData,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      const data = response.data;
      if (data) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("saveduserid", data.user._id);

        setUserLogin((prev) => {
          return {
            ...prev,
            _id: data.user._id,
          };
        });

        trackClarityEvent("tenant_basic_info_step_ended");
        setTimeout(() => {
          trackClarityEvent("tenant_document_upload_step_started");
        }, constants.CLARITY_DELAY);
      }
      const payload = {
        allDocuments: Array.isArray(selectedProtectedHousingDocs)
          ? selectedProtectedHousingDocs
          : [],
      };
      await submitProtectedHousingDocuments(protected_housing ? payload : []);
      if (cookies?.catellaWorkspaceTenant) {
        removeCookie("catellaWorkspaceTenant", { path: "/", maxAge: 1800 });
      }
      if (cookies?.libeenWorkspaceTenant) {
        removeCookie("libeenWorkspaceTenant", { path: "/", maxAge: 1800 });
      }

      if (haveValue(invitationToken) && response?.data?.auth === true) {
        removeCookie("invitationToken", { path: "/" });
        redirectFun(response.data);
      }
      if (isInvitedEmail && response?.data?.auth === true) {
        redirectFun(response.data);
      }
    } catch (error) {
      // console.log(error);
      const data = error?.response?.data;
      if (data?.status === "error" && data?.msg) {
        toast.error(data?.msg);
      } else if (haveValue(data?.key)) {
        toast.error(_t(data?.key));
      }
    }
  };

  if (currentScreen === "registerUser") {
    return (
      <BasicInfoModal
        formData={userLogin}
        setFormData={setUserLogin}
        createUserProfile={submitHandler}
        requiresSubcategory={requiresSubcategory}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        selectedProtectedHousingDocs={selectedProtectedHousingDocs}
        setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
        usedFor="register"
        backBtn={() => setCurrentScreen("emailScreen")}
        companyLogo={companyLogo}
        landlordsDataFromShareProfile={landlordsData}
        disableEmail={isInvitedEmail}
      />
    );
  }

  return (
    <div className="bigform">
      <div className="form-left tenant-new-form-left">
        <h2>
          {_t("makingHouseRentingEasyAnd")}
          <span> {_t("processSmooth")}</span>
        </h2>
      </div>
      <div className="form-right new-form-right overflow-y-scroll">
        <div className="form-inner">
          <div
            className={`logoWrapper ${
              haveValue(companyLogo) ? "withCompanyLogo" : ""
            }`}
          >
            <div className="form_logo">
              {haveValue(companyLogo) ? (
                <>
                  <img
                    className="company-logo"
                    src={`${process.env.REACT_APP_CDN_ENDPOINT}/${process.env.REACT_APP_CDN_CONTAINER}/${companyLogo}`}
                    alt=""
                  />
                  <div className="d-flex align-items-center justify-content-center custom-gap-2 poweredByWrapper">
                    <p className="poweredBy">{_t("poweredBy")}</p>
                    <img
                      src={Logo}
                      alt="Powered By Logo"
                      className="poweredByLogo"
                    />
                  </div>
                </>
              ) : (
                <img src={Logo} alt="logo" />
              )}
            </div>

            {currentLang == "es" ? (
              <div
                className="language-switch-container language-switch-container-es  cursor-pointer"
                onClick={() => handleLanguageChange("en-us")}
              >
                <p>ES</p>
                <img
                  className="flag-image"
                  alt="Flag"
                  src="/images/spain-flag.png"
                />
              </div>
            ) : (
              <div
                className="language-switch-container cursor-pointer"
                onClick={(e) => handleLanguageChange("es")}
              >
                <img className="flag-image" alt="Flag" src={Flag} />
                <p>EN</p>
              </div>
            )}
          </div>
          {currentScreen === "emailScreen" && (
            <EmailForm
              email={userLogin?.email ?? ""}
              onChangeHandler={onChangeHandler}
              disabled={disabled}
              emailSubmitHandler={emailSubmitHandler}
              companyLogo={companyLogo}
              usedFor={"tenant"}
              from={from}
            />
          )}
          {currentScreen === "verifyOTP" && (
            <EnterOtpModal
              onClose={() => setCurrentScreen("emailScreen")}
              email={userLogin?.email ?? ""}
              verifyOTPHandler={(e) => submitHandler(e, true)}
              otp={otp}
              setOtp={setOtp}
              showResend={showResend}
              showTimer={showTimer}
              sendOtpFun={sendOtpFun}
              verifyOtpLoading={verifyOtpLoading}
            />
          )}

          <p className="cookiesdiv">
            {_t(
              "byClickingNextYouAcceptAllThe",
              "Text used in onboarding process"
            )}
            <Link to="https://skortorent.com/terms-conditions.html">
              {_t("termsAndConditions", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/privacy-policy.html">
              {_t("privacyPolicy", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/cookies.html">
              {_t("cookiePolicy", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/legal-notice.html">
              {_t("legalNotice", "Text used in onboarding process")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantAuth;
